import CustomAutocomplete from "@/components/Form/InputFields/CustomAutocomplete";
import {
  AUDIENCE_REF_TYPES,
  AUDIENCE_TYPES,
} from "@/modules/Campaign/campaign.constant";
import { TName } from "@/types";
import { Box } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import { FieldValues, PathValue, useWatch } from "react-hook-form";
import {
  getSegmentIdsFromAudience,
  parseAudienceRefValue,
  toAudienceRefValue,
} from "../../../targetingExpression.helpers";
import { SubModule, SubModuleCommonProps } from "../../../targetingExpression.interface";

const moduleName = "user";
const filterName = "custom_segment";
export default function CustomSegment<
  T extends FieldValues,
  U extends SubModule = "CUSTOM_SEGMENT"
>({
  control,
  errors,
  setValue,
  getValues,
  parentName,
  audiencesLoading,
  audiences,
  isEdit
}: CustomSegmentProps<T, U>) {

  const commonName = `${parentName}.${moduleName}.${filterName}.value`
  const audienceType = useWatch({
    name: `audienceType` as TName<T>,
    control
  })

  function onAudienceTypeChange() {
    setValue(`audienceIds` as TName<T>, [] as PathValue<T, TName<T>>);
    resetSegmentIds()
  }

  const { data, isLoading, isError } =
    audiences[audienceType ?? AUDIENCE_TYPES.Audience]
  const selectedAudienceValues: (number | string)[] =
    useWatch({
      name: `audienceIds` as TName<T>,
      control,
    }) ?? []

  const onAudienceChange = (ids: undefined | (number | string)[]) => {
    const segmentIdFormKey = `${commonName}.segmentIds` as TName<T>
    if (!ids || !ids?.length) {
      resetSegmentIds()
      return
    }
    const segmentIds = getSegmentIdsFromAudience({ ids, data })
    setValue(segmentIdFormKey, segmentIds as PathValue<T, TName<T>>, {
      shouldDirty: true
    })
  }

  const resetSegmentIds = () => {
    const segmentIdFormKey = `${commonName}.segmentIds` as TName<T>
    const segmentIds = getValues(segmentIdFormKey)
    if (segmentIds.length) setValue(segmentIdFormKey, [] as PathValue<T, TName<T>>, {
      shouldDirty: true
    })
    return
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "auto",
        padding: 2,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <CustomAutocomplete
          name={`audienceType` as TName<T>}
          label="Audience Type"
          itemList={audienceTypeList}
          control={control}
          error={errors?.audienceType?.message}
          onChange={onAudienceTypeChange}
          disableClearable
        />
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <CustomAutocomplete
          key={audienceType}
          name={`audienceIds` as TName<T>}
          label="Audience"
          itemList={
            audienceType
              ? withFallbackOptions(
                  audienceListMapping(data),
                  selectedAudienceValues,
                  isLoading
                )
              : []
          }
          onChange={onAudienceChange}
          control={control}
          error={
            errors?.audienceIds?.message ??
            (isError
              ? "Some audiences failed to load — retry before changing this selection."
              : undefined)
          }
          loading={audiencesLoading || isLoading}
          // While the list is loading (or half of it failed) a change would rebuild
          // segmentIds from incomplete data and silently drop the missing
          // audiences' segments from the targeting expression.
          disabled={isLoading || isError}
          isMultiple
        />
      </Box>
    </Box>
  );
}
type AudienceKeys = typeof AUDIENCE_TYPES[keyof typeof AUDIENCE_TYPES]
type CustomSegmentProps<T extends FieldValues, U> = U extends "CUSTOM_SEGMENT"
  ? CustomSegment<T>
  : never;
interface CustomSegment<T extends FieldValues> extends SubModuleCommonProps<T> {
  audiencesLoading: boolean;
  audiences: {
    [key in AudienceKeys]: UseQueryResult<any, unknown>
  }
}


const audienceTypeList = [
  { label: "Account", value: AUDIENCE_TYPES.Audience },
  { label: "Contact", value: AUDIENCE_TYPES.EmailAudiences },
  { label: "Site Visitor", value: AUDIENCE_TYPES.RetargetAudience },
];

function audienceListMapping(data: any) {
  return data?.map((item: any) => ({
    // "Site Visitor" mixes two sources, so the row says which one it came from.
    label:
      item.audienceRefType === AUDIENCE_REF_TYPES.VisitorInsightAudience
        ? `${item.segmentName} (Visitor Insight)`
        : item.segmentName,
    // Ids repeat across audience tables — the type has to travel with the id.
    value: item.audienceRefType
      ? toAudienceRefValue(item.audienceRefType, item.id)
      : item.id,
  }));
}

// A saved selection can reference an audience the list cannot offer (1TO1 or
// re-processed Visitor Insight audiences, a deleted row). It must stay selectable
// so an unrelated edit does not silently drop it — the backend keeps its tracking
// row either way; only its segments cannot be recomputed client-side.
function withFallbackOptions(
  options: { label: string; value: number | string }[] | undefined,
  selectedValues: (number | string)[],
  isLoading: boolean
) {
  const known = new Set((options ?? []).map((option) => option.value));
  // While loading, everything selected looks "missing" — don't flash fallbacks.
  const missing = isLoading
    ? []
    : selectedValues.filter((value) => !known.has(value));
  return [
    ...(options ?? []),
    ...missing.map((value) => ({
      label: `Unavailable audience (${parseAudienceRefValue(value).id ?? value})`,
      value,
    })),
  ];
}

